import { MmcDataSource } from '../index';
import { configs } from '../configs';
import { memoizeWithAllArgsHashed as memoize } from '../utils';
import { ImportOrigin } from '../../constants';

const entityFactory = {
  getStoreGmcImportInfo: memoize(storeId =>
    new MmcDataSource({
      routePrefix: configs.routes.oDataRoute,
      entityName: 'GetStoreGmcImportInfo',
      endpoint: configs.endpoints.gmcMerchants(storeId),
    })),
  upsertSchedule: memoize((storeId, merchantId) =>
    new MmcDataSource({
      routePrefix: configs.routes.oDataRoute,
      entityName: 'UpsertSchedule',
      endpoint: configs.endpoints.gmcMerchant(storeId, merchantId),
    })),
  getGmcImportAuthInfo: memoize(storeId =>
    new MmcDataSource({
      routePrefix: configs.routes.oDataRoute,
      entityName: 'GetGmcImportAuthInfo',
      endpoint: configs.endpoints.gmcMerchants(storeId),
    })),
  signOutGmcImport: memoize(storeId =>
    new MmcDataSource({
      routePrefix: configs.routes.oDataRoute,
      entityName: 'SignOutGmcImport',
      endpoint: configs.endpoints.gmcMerchants(storeId),
    })),
  getGmcMerchants: memoize((storeId, domain) =>
    new MmcDataSource({
      routePrefix: configs.routes.oDataRoute,
      entityName: '',
      endpoint: configs.endpoints.getGmcMerchants(storeId, domain),
    })),
  getImportFeeds: memoize((storeId, merchantId) =>
    new MmcDataSource({
      routePrefix: configs.routes.oDataRoute,
      entityName: 'GetImportFeeds',
      endpoint: configs.endpoints.gmcMerchant(storeId, merchantId),
    })),
  getGmcImportSchedule: memoize((storeId, merchantId) =>
    new MmcDataSource({
      routePrefix: configs.routes.oDataRoute,
      entityName: 'GetSchedule',
      endpoint: configs.endpoints.gmcMerchant(storeId, merchantId),
    })),
  importFeeds: memoize((storeId, merchantId) =>
    new MmcDataSource({
      routePrefix: configs.routes.oDataRoute,
      entityName: 'EnqueueImportTask',
      endpoint: configs.endpoints.gmcMerchant(storeId, merchantId),
    })),
  updateScheduleStatus: memoize((storeId, merchantId) =>
    new MmcDataSource({
      routePrefix: configs.routes.oDataRoute,
      entityName: 'UpdateScheduleStatus',
      endpoint: configs.endpoints.gmcMerchant(storeId, merchantId),
    })),
  deleteSchedule: memoize((storeId, merchantId) =>
    new MmcDataSource({
      routePrefix: configs.routes.oDataRoute,
      entityName: 'DeleteSchedule',
      endpoint: configs.endpoints.gmcMerchant(storeId, merchantId),
    })),
  updateSchedule: memoize((storeId, merchantId) =>
    new MmcDataSource({
      routePrefix: configs.routes.oDataRoute,
      entityName: 'UpdateSchedule',
      endpoint: configs.endpoints.gmcMerchant(storeId, merchantId),
    })),
  updateFeedSchedule: memoize((storeId, merchantId) =>
    new MmcDataSource({
      routePrefix: configs.routes.oDataRoute,
      entityName: 'UpdateFeedSchedule',
      endpoint: configs.endpoints.gmcMerchant(storeId, merchantId),
    })),
  getUnavailableScheduleNames: memoize((storeId, merchantId) =>
    new MmcDataSource({
      routePrefix: configs.routes.oDataRoute,
      entityName: 'GetUnavailableScheduleNames',
      endpoint: configs.endpoints.gmcMerchant(storeId, merchantId),
    })),
  getImportDetails: memoize((storeId, origin) =>
    new MmcDataSource({
      routePrefix: configs.routes.oDataRoute,
      entityName: `GetImportDetails?origin=${origin}`,
      endpoint: configs.endpoints.imports(storeId),
    })),
  getImportFeedLabel: memoize((storeId, origin) =>
    new MmcDataSource({
      routePrefix: configs.routes.oDataRoute,
      entityName: `GetImportFeedLabel?origin=${origin}`,
      endpoint: configs.endpoints.imports(storeId),
    })),
  updateImport: memoize((storeId, merchantId, origin) =>
    new MmcDataSource({
      routePrefix: configs.routes.oDataRoute,
      entityName: `UpdateImport?origin=${origin}&merchantId=${merchantId}`,
      endpoint: configs.endpoints.imports(storeId),
    })),
  deleteImport: memoize((storeId, merchantId, origin) =>
    new MmcDataSource({
      routePrefix: configs.routes.oDataRoute,
      entityName: `DeleteImport?origin=${origin}&merchantId=${merchantId}`,
      endpoint: configs.endpoints.imports(storeId),
    })),
  triggerImport: memoize((storeId, merchantId, origin) =>
    new MmcDataSource({
      routePrefix: configs.routes.oDataRoute,
      entityName: `ScheduleImportTask?origin=${origin}&merchantId=${merchantId}`,
      endpoint: configs.endpoints.imports(storeId),
    })),
  setupImport: memoize((storeId, merchantId, origin) =>
    new MmcDataSource({
      routePrefix: configs.routes.oDataRoute,
      entityName: `SetupImport?origin=${origin}&merchantId=${merchantId}`,
      endpoint: configs.endpoints.imports(storeId),
    })),
  upsertAuthTokenForFeedImport: memoize(storeId =>
    new MmcDataSource({
      routePrefix: configs.routes.oDataRoute,
      entityName: 'UpsertAuthTokenForFeedImport',
      endpoint: configs.endpoints.imports(storeId),
    })),
  getMigrationStatus: memoize(storeId =>
    new MmcDataSource({
      routePrefix: configs.routes.oDataRoute,
      entityName: 'GetMigrationStatus',
      endpoint: configs.endpoints.imports(storeId),
    })),
  triggerMigration: memoize(storeId =>
    new MmcDataSource({
      routePrefix: configs.routes.oDataRoute,
      entityName: 'MigrateProvider',
      endpoint: configs.endpoints.imports(storeId),
    })),
  confirmMigration: memoize(storeId =>
    new MmcDataSource({
      routePrefix: configs.routes.oDataRoute,
      entityName: 'ConfirmMigration',
      endpoint: configs.endpoints.imports(storeId),
    })),
};

export const importQueries = {
  getStoreGmcImportInfo(storeId) {
    const entity = entityFactory.getStoreGmcImportInfo(storeId);

    return entity.find();
  },

  upsertMerchantSchedule(storeId, merchantId, payload) {
    const entity = entityFactory.upsertSchedule(storeId, merchantId);

    return entity.create(payload);
  },

  getGmcImportAuthInfo(storeId) {
    const entity = entityFactory.getGmcImportAuthInfo(storeId);

    return entity.find();
  },

  signOutGmcImport(storeId) {
    const entity = entityFactory.signOutGmcImport(storeId);

    return entity.create({});
  },

  getGmcMerchants(storeId, domain) {
    const entity = entityFactory.getGmcMerchants(storeId, domain);

    return entity.find();
  },

  getImportFeeds(storeId, merchantId) {
    const entity = entityFactory.getImportFeeds(storeId, merchantId);

    return entity.find();
  },

  getGmcImportSchedule(storeId, merchantId) {
    const entity = entityFactory.getGmcImportSchedule(storeId, merchantId);

    return entity.find();
  },

  importFeeds(storeId, merchantId, payload) {
    const entity = entityFactory.importFeeds(storeId, merchantId);

    return entity.create(payload);
  },

  updateScheduleStatus(storeId, merchantId, payload) {
    const entity = entityFactory.updateScheduleStatus(storeId, merchantId);

    return entity.update(null, payload);
  },

  deleteSchedule(storeId, merchantId) {
    const entity = entityFactory.deleteSchedule(storeId, merchantId);

    return entity.create({});
  },

  updateSchedule(storeId, merchantId, payload) {
    const entity = entityFactory.updateSchedule(storeId, merchantId);

    return entity.update(null, payload);
  },

  updateFeedSchedule(storeId, merchantId, payload) {
    const entity = entityFactory.updateFeedSchedule(storeId, merchantId);

    return entity.update(null, payload);
  },

  getUnavailableScheduleNames(storeId, merchantId) {
    const entity = entityFactory.getUnavailableScheduleNames(storeId, merchantId);

    return entity.find();
  },

  getImportDetails(storeId, origin = ImportOrigin.Google) {
    const entity = entityFactory.getImportDetails(storeId, origin);

    return entity.find();
  },

  getImportFeedLabel(storeId, page, pageSize, orderBy = null, origin = ImportOrigin.Google) {
    const entity = entityFactory.getImportFeedLabel(storeId, origin);

    const params = {
      $top: pageSize,
      $skip: page < 1 ? 0 : pageSize * (page - 1),
    };

    if (orderBy) {
      params.$orderby = orderBy;
    }

    return entity.findAll({ query: params });
  },

  updateImport(storeId, merchantId, payload, origin = ImportOrigin.Google) {
    const entity = entityFactory.updateImport(storeId, merchantId, origin);

    return entity.post(null, payload);
  },

  deleteImport(storeId, merchantId, origin = ImportOrigin.Google) {
    const entity = entityFactory.deleteImport(storeId, merchantId, origin);

    return entity.delete();
  },

  triggerImport(storeId, merchantId, payload, origin = ImportOrigin.Google) {
    const entity = entityFactory.triggerImport(storeId, merchantId, origin);

    return entity.post(null, payload);
  },

  setupImport(storeId, merchantId, payload, origin = ImportOrigin.Google) {
    const entity = entityFactory.setupImport(storeId, merchantId, origin);

    return entity.create(payload);
  },

  upsertAuthTokenForFeedImport(storeId, payload) {
    const entity = entityFactory.upsertAuthTokenForFeedImport(storeId);

    return entity.post(null, payload);
  },

  getMigrationStatus(storeId) {
    const entity = entityFactory.getMigrationStatus(storeId);

    return entity.find();
  },

  triggerMigration(storeId) {
    const entity = entityFactory.triggerMigration(storeId);

    return entity.post();
  },

  confirmMigration(storeId) {
    const entity = entityFactory.confirmMigration(storeId);

    return entity.post();
  },
};
